import { z } from "zod";
import { TimeOffTypeSchema, TimeOffResponseSchema } from "./timeOff.js";
import { uuidSchema } from "./validation-common.js";
// ===== ENUMS =====
export const ApprovalStatusSchema = z.enum(["PENDING", "APPROVED", "REJECTED"]);
export const ApprovalRequestTypeSchema = z.enum([
    "TIME_OFF",
    "SWAP_RECIPIENT",
    "SWAP_MANAGER",
    "SHIFT_PICKUP",
    "SHIFT_DROP",
    "UNPLANNED_SHIFT",
    "EARLY_CLOCK_IN",
]);
// ===== TYPE-SPECIFIC DATA PAYLOADS =====
export const TimeOffDataSchema = z.object({
    timeOffId: uuidSchema, // Reference to TimeOff record (source of truth)
});
export const SwapDataSchema = z.object({
    shiftId: uuidSchema,
    fromEmployeeId: uuidSchema,
    toEmployeeId: uuidSchema,
});
export const ShiftPickupDataSchema = z.object({
    shiftId: uuidSchema,
    employeeId: uuidSchema,
});
export const ShiftDropDataSchema = z.object({
    shiftId: uuidSchema,
    employeeId: uuidSchema,
});
export const UnplannedShiftDataSchema = z.object({
    shiftId: uuidSchema,
    employeeId: uuidSchema,
    locationId: uuidSchema,
    jobRoleFamilyId: uuidSchema,
    notes: z.string(),
});
export const EarlyClockInDataSchema = z.object({
    shiftId: uuidSchema,
    employeeId: uuidSchema,
    originalStartTime: z.iso.datetime(),
    actualClockInTime: z.iso.datetime(),
});
// ===== RESPONSE DATA SCHEMAS (enriched versions for API responses) =====
export const TimeOffResponseDataSchema = z.object({
    timeOffId: uuidSchema.optional(),
    timeOffType: TimeOffTypeSchema.optional(),
    startTime: z.iso.datetime().optional(),
    endTime: z.iso.datetime().optional(),
    availableBalanceMinutes: z.number().nullable().optional(),
});
// Shared shift detail fields for enriched responses
export const ShiftDetailsEnrichmentSchema = z.object({
    shiftStartTime: z.iso.datetime().optional(),
    shiftEndTime: z.iso.datetime().optional(),
    shiftLocationAddress: z.string().optional(),
    shiftLocationCityStateZip: z.string().optional(),
    shiftJobRoleName: z.string().optional(),
    shiftJobRoleFamilyName: z.string().optional(),
});
export const SwapResponseDataSchema = SwapDataSchema.merge(ShiftDetailsEnrichmentSchema).extend({
    fromEmployeeName: z.string().optional(),
});
export const ShiftPickupResponseDataSchema = ShiftPickupDataSchema.merge(ShiftDetailsEnrichmentSchema);
export const ShiftDropResponseDataSchema = ShiftDropDataSchema.merge(ShiftDetailsEnrichmentSchema);
export const UnplannedShiftResponseDataSchema = UnplannedShiftDataSchema.merge(ShiftDetailsEnrichmentSchema);
export const EarlyClockInResponseDataSchema = EarlyClockInDataSchema.merge(ShiftDetailsEnrichmentSchema);
// Map request types to their data schemas for validation
export const ApprovalRequestDataSchemas = {
    TIME_OFF: TimeOffDataSchema,
    SWAP_RECIPIENT: SwapDataSchema,
    SWAP_MANAGER: SwapDataSchema,
    SHIFT_PICKUP: ShiftPickupDataSchema,
    SHIFT_DROP: ShiftDropDataSchema,
    UNPLANNED_SHIFT: UnplannedShiftDataSchema,
    EARLY_CLOCK_IN: EarlyClockInDataSchema,
};
// ===== API TYPES =====
const BaseApprovalRequestResponseFields = {
    id: z.uuid(),
    status: ApprovalStatusSchema,
    requesterId: z.uuid(),
    approverId: z.uuid().nullish(),
    approverLocationId: z.uuid().nullish(),
    responderId: z.uuid().nullish(),
    comment: z.string().nullish(),
    responseComment: z.string().nullish(),
    parentRequestId: z.uuid().nullish(),
    companyId: z.uuid(),
    requesterName: z.string().nullish(),
    responderName: z.string().nullish(),
    requestForDate: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    respondedAt: z.iso.datetime().nullish(),
};
export const ApprovalRequestResponseSchema = z.discriminatedUnion("requestType", [
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("TIME_OFF"),
        data: TimeOffResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("SWAP_RECIPIENT"),
        data: SwapResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("SWAP_MANAGER"),
        data: SwapResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("SHIFT_PICKUP"),
        data: ShiftPickupResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("SHIFT_DROP"),
        data: ShiftDropResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("UNPLANNED_SHIFT"),
        data: UnplannedShiftResponseDataSchema,
    }),
    z.object({
        ...BaseApprovalRequestResponseFields,
        requestType: z.literal("EARLY_CLOCK_IN"),
        data: EarlyClockInResponseDataSchema,
    }),
]);
/**
 * Schema for time off with approval request response
 * Used by POST /time-off/requests endpoint that returns both timeOff and approvalRequest
 */
export const TimeOffWithApprovalRequestResponseSchema = z.object({
    timeOff: TimeOffResponseSchema,
    approvalRequest: ApprovalRequestResponseSchema,
});
// Count by type response
export const ApprovalRequestCountByTypeSchema = z.object({
    requestType: ApprovalRequestTypeSchema,
    count: z.number().int().nonnegative(),
});
export const ApprovalRequestCountsByTypeResponseSchema = z.array(ApprovalRequestCountByTypeSchema);
// Query schema for counts-pending endpoint
export const GetPendingCountsOrpcQuerySchema = z.object({
    approverLocationId: z.uuid().optional(),
});
const ResponseDataSchemaMap = {
    TIME_OFF: TimeOffResponseDataSchema,
    SWAP_RECIPIENT: SwapResponseDataSchema,
    SWAP_MANAGER: SwapResponseDataSchema,
    SHIFT_PICKUP: ShiftPickupResponseDataSchema,
    SHIFT_DROP: ShiftDropResponseDataSchema,
    UNPLANNED_SHIFT: UnplannedShiftResponseDataSchema,
    EARLY_CLOCK_IN: EarlyClockInResponseDataSchema,
};
export function narrowRequestData(requestType, data) {
    const schema = ResponseDataSchemaMap[requestType];
    return schema.parse(data);
}
// ===== INPUT SCHEMAS =====
// Base fields shared by all request types
const BaseApprovalRequestInputSchema = z.object({
    requesterId: uuidSchema,
    approverId: uuidSchema.optional(), // For peer-to-peer approvals (SWAP_RECIPIENT)
    approverLocationId: uuidSchema.optional(), // For location-based approvals (SHIFT_PICKUP, SHIFT_DROP, SWAP_MANAGER)
    comment: z.string().max(1000).optional(),
    parentRequestId: uuidSchema.optional(),
    companyId: uuidSchema,
});
// Discriminated union for type-safe input validation
export const CreateApprovalRequestInputSchema = z.discriminatedUnion("requestType", [
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("TIME_OFF"),
        data: TimeOffDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("SWAP_RECIPIENT"),
        data: SwapDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("SWAP_MANAGER"),
        data: SwapDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("SHIFT_PICKUP"),
        data: ShiftPickupDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("SHIFT_DROP"),
        data: ShiftDropDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("UNPLANNED_SHIFT"),
        data: UnplannedShiftDataSchema,
    }),
    BaseApprovalRequestInputSchema.extend({
        requestType: z.literal("EARLY_CLOCK_IN"),
        data: EarlyClockInDataSchema,
    }),
]);
export const RespondToApprovalRequestInputSchema = z.object({
    status: z.enum(["APPROVED", "REJECTED"]),
    responseComment: z.string().max(1000).optional(),
});
// ===== QUERY SCHEMAS =====
export const GetApprovalRequestsQuerySchema = z.object({
    companyId: uuidSchema,
    approverLocationId: uuidSchema.optional(),
    approverEmploymentId: uuidSchema.optional(),
    requesterId: uuidSchema.optional(),
    status: ApprovalStatusSchema.optional(),
    requestType: ApprovalRequestTypeSchema.optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
});
// ============================================
// oRPC Input Schemas (companyId from JWT)
// ============================================
/**
 * Create approval request input for oRPC
 * companyId is extracted from JWT, not from request body
 */
export const CreateApprovalRequestOrpcInputSchema = z.discriminatedUnion("requestType", [
    z.object({
        requestType: z.literal("TIME_OFF"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: TimeOffDataSchema,
    }),
    z.object({
        requestType: z.literal("SWAP_RECIPIENT"),
        approverId: uuidSchema, // Peer-to-peer approval
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: SwapDataSchema,
    }),
    z.object({
        requestType: z.literal("SWAP_MANAGER"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: SwapDataSchema,
    }),
    z.object({
        requestType: z.literal("SHIFT_PICKUP"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: ShiftPickupDataSchema,
    }),
    z.object({
        requestType: z.literal("SHIFT_DROP"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: ShiftDropDataSchema,
    }),
    z.object({
        requestType: z.literal("UNPLANNED_SHIFT"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: UnplannedShiftDataSchema,
    }),
    z.object({
        requestType: z.literal("EARLY_CLOCK_IN"),
        approverLocationId: uuidSchema,
        comment: z.string().max(1000).optional(),
        parentRequestId: uuidSchema.optional(),
        data: EarlyClockInDataSchema,
    }),
]);
;
/**
 * Query params for listing approval requests via oRPC
 * companyId is extracted from JWT, not from query params
 */
export const GetApprovalRequestsOrpcQuerySchema = z.object({
    approverLocationId: uuidSchema.optional(),
    status: ApprovalStatusSchema.optional(),
    requestType: ApprovalRequestTypeSchema.optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
});
/**
 * Query params for listing user's own approval requests via oRPC
 * Only status and requestType filters - userId comes from JWT
 */
export const GetMyApprovalRequestsOrpcQuerySchema = z.object({
    status: ApprovalStatusSchema.optional(),
    requestType: ApprovalRequestTypeSchema.optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
});
/**
 * Query params for listing user's own responses via oRPC
 * Only status and requestType filters - responderId comes from JWT
 */
export const GetMyResponsesOrpcQuerySchema = z.object({
    status: ApprovalStatusSchema.optional(),
    requestType: ApprovalRequestTypeSchema.optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
});
export const ApprovalRequestIdParamSchema = z.object({
    id: uuidSchema,
});
